/**
 * Frames that lay out what they hold (Figma's auto layout: `style.layout` row or column, with
 * `gap`, `padding`, `align`): after a gesture or a change of the frame's layout, the items it
 * holds are written where the layout shows them, so the next drag reorders as seen.
 */
import { unwrap } from "solid-js/store";
import type { BoardPlace } from "@protocol/BoardPlace";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { BoardCtx } from "./context";
import { asPatch, boardOf } from "./geometry";

/** Whether frame `id` lays out what it holds (its `style.layout`). */
export function laysOut(ctx: BoardCtx, id: string, style = ctx.groupOf(id)?.style): boolean {
  return !!style?.layout && !!ctx.groupOf(id)?.board;
}

/**
 * The edits keeping what a layout frame holds where the layout shows it: `places` (a gesture's,
 * by id) decide the order with everything else where it is shown, `homes` the frame each moved
 * node ends in, `style` the frame's style if it changes. Kept places are then what is shown, so
 * the next drag past an item reorders as seen. Written into `patches` for the nodes already
 * edited, else as edits of their own.
 */
export function relayEdits(
  ctx: BoardCtx,
  frame: string,
  places: Map<string, BoardPlace>,
  homes: Map<string, string | null> = new Map(),
  patches: Map<string, Record<string, unknown>> = new Map(),
  style?: { [key: string]: string },
): GraphEdit[] {
  const g = unwrap(ctx.deps.graph.state.graph);
  const shown = ctx.content().places;
  const nodes = g.nodes.map((n) => {
    const group = homes.has(n.id) ? homes.get(n.id)! : (n.group ?? null);
    const board = places.get(n.id) ?? (group === frame ? shown.get(n.id) : undefined) ?? n.board;
    return { ...n, group, ...(board ? { board } : {}) };
  });
  const groups = g.groups.map((x) => ({ ...x, ...(places.has(x.id) ? { board: places.get(x.id)! } : {}), ...(style && x.id === frame ? { style: { ...x.style, ...style } } : {}) }));
  const laid = boardOf({ ...g, nodes, groups }).places;
  const edits: GraphEdit[] = [];
  for (const n of nodes) {
    const place = n.group === frame ? laid.get(n.id) : undefined;
    if (!place) continue;
    const board = asPatch({ ...place, z: n.board?.z });
    const patch = patches.get(n.id);
    if (patch) patch.board = board;
    else if (JSON.stringify(board) !== JSON.stringify(ctx.nodeOf(n.id)?.board ? asPatch(ctx.nodeOf(n.id)!.board!) : null)) edits.push({ edit: "update_node", id: n.id, patch: { board } });
  }
  return edits;
}
